import type { FC } from "react"
import React from "react"
import { isAddress } from "@ethersproject/address"
import type { StackProps } from "@chakra-ui/react"
import { Stack, Code, Flex, Link, Text, HStack, Icon } from "@chakra-ui/react"

import { shortenIfAddress } from "web3/helpers/address"
import { useDevice } from "common/hooks/useDevice"
import type { Calldata } from "proposal/types/calldata"
import type { ChainId, Maybe } from "query/graphql"
import { getChainIdParams, getExplorerAddressLink } from "web3/helpers/chainId"
import type { DecodeError } from "proposal/hooks/useParseProposalAction"
import {
  errors,
  useParseProposalAction,
} from "proposal/hooks/useParseProposalAction"
import WarningIcon from "ui/components/icons/WarningIcon"
import { formatProposalValues } from "common/helpers/formatProposalValues"
import { getChainByReference } from "web3/helpers/findChain"
import { parseAddressFromTarget } from "web3/helpers/transformers"

type Props = {
  calldata: string
  chainId: ChainId
  index?: number
  signature?: Maybe<string>
  target: string
  value?: Maybe<string>
  isDraftProposal?: boolean
}

const ProposalCommand: FC<Props & StackProps> = ({
  calldata,
  chainId,
  index,
  signature,
  target,
  value,
  isDraftProposal = false,
  ...stackProps
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  const { address: targetAddress } = parseAddressFromTarget(target)

  const { status, decodedCalldata, derivedSignature, isSendAssetRecipe } =
    useParseProposalAction({
      chainId,
      calldata,
      target: targetAddress,
      signature,
    })

  const { reference } = getChainIdParams(chainId)
  const chainData = getChainByReference(reference)
  const href = chainData ? getExplorerAddressLink(chainData, targetAddress) : ""

  return (
    <Stack align="flex-start" spacing={3} w="full">
      <HStack justify="space-between" w="full">
        {index !== undefined && index >= 0 ? (
          <Text textStyle="md">{`Function ${index + 1}:`}</Text>
        ) : (
          <Text />
        )}
      </HStack>
      <Stack
        border="gray.dark"
        maxW="3xl"
        overflowWrap="break-word"
        p={6}
        rounded="sm"
        spacing={6}
        w="full"
        {...stackProps}
      >
        <Warning error={status?.error} />
        <Signature signature={derivedSignature} />
        <DecodedCalldata
          calldata={calldata}
          chainReference={reference}
          decodedCalldata={decodedCalldata}
          isDraftProposal={isDraftProposal}
          isSendAssetRecipe={isSendAssetRecipe}
        />
        <Flex direction="column">
          <Code as="label" color="gray.600" textStyle="code">
            Target:
          </Code>
          <Link
            isExternal
            color="gray.400"
            display={onLargeDevice}
            href={href}
            textStyle="code"
          >
            {targetAddress}
          </Link>
          <Link
            isExternal
            color="gray.400"
            display={onLittleDevice}
            href={href}
            textStyle="code"
          >
            {shortenIfAddress(targetAddress)}
          </Link>
        </Flex>
        <Flex direction="column">
          <Code color="gray.600" textStyle="code">
            Value:
          </Code>
          <Code color="gray.400" textStyle="code">{`${value}`}</Code>
        </Flex>
      </Stack>
    </Stack>
  )
}

export default ProposalCommand

type WarningProps = {
  error?: DecodeError
}

const Warning: FC<WarningProps> = ({ error }) => {
  if (!error) return null

  const errorLabel = errors[error]

  return (
    <HStack>
      <Icon as={WarningIcon} h={4} w={4} />
      <Text color="gray.500" textStyle="body.regular.sm">
        {errorLabel}
        &nbsp;Calldata cannot be decoded.
      </Text>
    </HStack>
  )
}

type SignatureProps = {
  signature?: string
}

const Signature: FC<SignatureProps> = ({ signature }) => {
  if (!signature) return null

  return (
    <Flex direction="column">
      <Code as="label" color="gray.600" textStyle="code">
        Signature:
      </Code>
      <Code color="gray.400" textStyle="code">
        {signature}
      </Code>
    </Flex>
  )
}

type DecodedCalldataProps = {
  calldata: string
  chainReference: number
  decodedCalldata?: Calldata[]
  isSendAssetRecipe: boolean
  isDraftProposal?: boolean
}

const DecodedCalldata: FC<DecodedCalldataProps> = ({
  calldata,
  chainReference,
  decodedCalldata,
  isSendAssetRecipe,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  if (isSendAssetRecipe) return null

  if (!decodedCalldata) {
    return (
      <Flex direction="column">
        <Code as="label" color="gray.600" textStyle="code">
          Calldata:
        </Code>
        <Stack isInline align="center" className="clamp-text-3">
          <Code className="clamp-text-3" color="gray.400" textStyle="code">
            {calldata}
          </Code>
        </Stack>
      </Flex>
    )
  }

  if (!decodedCalldata.length) return null

  return (
    <Flex direction="column">
      <Code as="label" color="gray.600" textStyle="code">
        Calldata:
      </Code>
      <Stack spacing={0.5}>
        {decodedCalldata.map(({ type, value }) => {
          const chainData = getChainByReference(chainReference)
          const href = chainData ? getExplorerAddressLink(chainData, value) : ""
          const displayValue = formatProposalValues(value, type)

          return (
            <HStack key={value} align="flex-start" overflowX="auto">
              <Code
                as="label"
                className="clamp-text-3"
                color="gray.600"
                minW="10%"
                pt={2}
                textStyle="code"
              >
                {type}:
              </Code>
              {isAddress(value) ? (
                <Stack pt={2} spacing={0}>
                  <Link
                    isExternal
                    color="gray.400"
                    href={href}
                    textStyle="code"
                  >
                    <Code display={onLargeDevice} textStyle="code">
                      {value}
                    </Code>
                    <Code display={onLittleDevice} textStyle="code">
                      {shortenIfAddress(value)}
                    </Code>
                  </Link>
                </Stack>
              ) : (
                <Stack pt={2} spacing={0}>
                  <Code
                    className="clamp-text-3"
                    color="gray.400"
                    maxW="100%"
                    textStyle="code"
                    wordBreak="break-word"
                  >
                    {`${displayValue}`}
                  </Code>
                </Stack>
              )}
            </HStack>
          )
        })}
      </Stack>
    </Flex>
  )
}
